/**
 * Title Scene
 * Game title, start button, and high score display
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';
import { stateManager } from '../systems/StateManager.js';

export class TitleScene extends Phaser.Scene {
    constructor() {
        super({ key: 'TitleScene' });
    }

    create() {
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height / 2;

        // Background - starts bright (honeypot anticipation)
        this.cameras.main.setBackgroundColor(CONSTANTS.COLORS.BRIGHT_BG);

        // Title text with subtle animation
        this.titleText = this.add.text(centerX, centerY - 150, 'QUANTATO', {
            fontSize: '72px',
            fontFamily: 'Georgia, serif',
            color: '#FFD93D',
            stroke: '#333333',
            strokeThickness: 4
        }).setOrigin(0.5);

        this.subtitleText = this.add.text(centerX, centerY - 80, 'THE OUTLIER', {
            fontSize: '28px',
            fontFamily: 'Courier New, monospace',
            color: '#333333',
            letterSpacing: 8
        }).setOrigin(0.5);

        // Mascot preview
        this.mascot = this.add.image(centerX, centerY + 50, 'quantato_happy').setScale(1.2);

        // Subtle floating animation
        this.tweens.add({
            targets: this.mascot,
            y: centerY + 40,
            duration: 2000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Start button
        this.createStartButton();

        // High score display
        this.createHighScoreDisplay();

        // Instructions
        this.add.text(centerX, this.scale.height - 50, 'A math learning game... or is it?', {
            fontSize: '14px',
            fontFamily: 'Arial',
            color: '#666666',
            fontStyle: 'italic'
        }).setOrigin(0.5);

        // Title glitch effect (subtle foreshadowing)
        this.glitchTimer = 0;
        this.glitchInterval = Phaser.Math.Between(8000, 15000);
    }

    createStartButton() {
        const centerX = this.scale.width / 2;
        const buttonY = this.scale.height / 2 + 150;

        // Button background
        this.startButton = this.add.image(centerX, buttonY, 'button_normal')
            .setInteractive({ useHandCursor: true });

        // Button text
        this.startText = this.add.text(centerX, buttonY, 'START', {
            fontSize: '24px',
            fontFamily: 'Arial',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Hover effects
        this.startButton.on('pointerover', () => {
            this.startButton.setTexture('button_hover');
            this.startButton.setScale(1.05);
            this.startText.setScale(1.05);
        });

        this.startButton.on('pointerout', () => {
            this.startButton.setTexture('button_normal');
            this.startButton.setScale(1);
            this.startText.setScale(1);
        });

        // Click handler
        this.startButton.on('pointerdown', () => {
            audioEngine.resume();
            audioEngine.playClick();

            // Brief press animation
            this.startButton.setScale(0.95);
            this.startText.setScale(0.95);

            this.time.delayedCall(100, () => {
                this.cameras.main.fadeOut(500, 0, 0, 0);
                this.time.delayedCall(500, () => {
                    this.scene.start('HoneypotScene');
                });
            });
        });
    }

    createHighScoreDisplay() {
        const highScore = stateManager.getHighScore();

        if (highScore > 0) {
            this.add.text(this.scale.width / 2, this.scale.height / 2 + 200, `High Score: ${highScore}`, {
                fontSize: '18px',
                fontFamily: 'Courier New, monospace',
                color: '#666666'
            }).setOrigin(0.5);
        }
    }

    update(time, delta) {
        // Occasional subtle glitch on title (foreshadowing)
        this.glitchTimer += delta;

        if (this.glitchTimer >= this.glitchInterval) {
            this.glitchTimer = 0;
            this.glitchInterval = Phaser.Math.Between(8000, 15000);
            this.playTitleGlitch();
        }
    }

    playTitleGlitch() {
        // Brief position offset
        const originalX = this.titleText.x;
        const originalColor = this.titleText.style.color;

        this.titleText.x += Phaser.Math.Between(-5, 5);
        this.titleText.setColor('#FF0000');

        this.time.delayedCall(50, () => {
            this.titleText.x = originalX;
            this.titleText.setColor(originalColor);
        });

        // Brief subtitle flicker
        this.subtitleText.setAlpha(0.3);
        this.time.delayedCall(80, () => {
            this.subtitleText.setAlpha(1);
        });
    }
}
